import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { UserProfileService, UserProfileVo } from './user-profile.service';
import { BannerAlertComponent } from '../../components/banner-alert/banner-alert.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PopupDialogComponent } from '../../components/popup-dialog/popup-dialog.component';
import { AuthContext } from '../../auth/services/auth.context';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatCardModule,
    BannerAlertComponent
  ],
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {

  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private authContext = inject(AuthContext);

  userProfileForm: FormGroup;
  canDeactivateAccount = false;
  isDeactivatingAccount = false;

  constructor(
    private router: Router,
    private userProfileService: UserProfileService,
  ) {
    // Initialize the form group
    this.userProfileForm = new FormGroup({
      firstName: new FormControl('', [Validators.required, Validators.pattern('^[A-Za-z]+(\\s[A-Za-z]+)*$'), Validators.maxLength(50)]),
      lastName: new FormControl('', [Validators.pattern('^[A-Za-z]+(\\s[A-Za-z]+)*$'), Validators.maxLength(50)]), // Adjusted pattern for max length
      mobileNumber: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.pattern('[0-9]{10}')]),
      emailAddress: new FormControl({ value: '', disabled: true }, [Validators.email]),
      alternateMobileNumber: new FormControl('', [Validators.pattern(/^[1-9]\d{9}$/)]),
    });
  }

  ngOnInit(): void {
    const userInfo = this.authContext.getUserInfo();
    this.canDeactivateAccount = userInfo?.userType === 'ROLE_USER';
    this.getUserProfile();
  }

  onSubmit() {
    if (this.userProfileForm.valid) {
      // Include all fields in the payload
      const payload = {
        ...this.userProfileForm.value,
        mobileNumber: this.userProfileForm.get('mobileNumber')?.value, // Get the value explicitly
        emailAddress: this.userProfileForm.get('emailAddress')?.value // Get the value explicitly
      };
      this.userProfileService.updateUserProfile(payload).subscribe({
        next: (response) => {
          if (response) {
            // console.log(response);
            this.openSnackBar("User profile details saved successfully", "Okay");
            this.router.navigate(['/app/home'], { replaceUrl: true });
          }
        },
        error: (error) => {
          console.error('POST error:', error);
        },
      });
    }
  }

  backToHome() {
    this.router.navigate(['/app/home']);
  }

  getUserProfile() {
    this.userProfileService.getUserInfo().subscribe({
      next: (response) => {
        if (response) {
          // console.log(response);
          this.userProfileForm.patchValue(response); // Update the form with user data
        }
      },
      error: (error) => {
        console.error('GET error:', error);
      },
    });
  }

  openDeactivateAccountDialog(): void {
    if (!this.canDeactivateAccount || this.isDeactivatingAccount) {
      return;
    }

    const dialogRef = this.dialog.open(PopupDialogComponent, {
      data: {
        event: 'DEACTIVATE_ACCOUNT',
        message: 'Deactivating your account will end your active location allocations and disable your login access. You will be signed out immediately, and only an administrator can restore access later. Do you want to continue?',
        title: 'Deactivate My Account'
      },
      width: '520px',
      height: '240px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.deactivateAccount();
      }
    });
  }

  private deactivateAccount(): void {
    this.isDeactivatingAccount = true;
    this.userProfileService.deactivateAccount().subscribe({
      next: (response) => {
        this.isDeactivatingAccount = false;
        if (response?.valid) {
          this.openSnackBar(response.message || 'Your account has been deactivated successfully.', 'Okay');
          localStorage.clear();
          this.router.navigate(['/app/login'], { replaceUrl: true });
          return;
        }

        this.openSnackBar(response?.message || 'Failed to deactivate account.', 'Okay');
      },
      error: (error) => {
        this.isDeactivatingAccount = false;
        console.error('POST error:', error);
        this.openSnackBar(error?.error?.message || 'Failed to deactivate account.', 'Okay');
      },
    });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }
}
